import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";
import ArticleCard from "@/components/feature/ArticleCard";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import HotRankList from "@/components/feature/HotRankList";
import { useSeo } from "@/hooks/useSeo";

type SortKey = "latest" | "hot";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((item) => item.slug === slug);
  const [sort, setSort] = useState<SortKey>("latest");

  const list = useMemo(() => {
    const base = articles.filter((item) => item.categorySlug === slug);
    return sort === "hot" ? [...base].sort((a, b) => b.heat - a.heat) : base;
  }, [slug, sort]);

  useSeo({
    title: category
      ? `${category.name}热点事件汇总 | 52吃瓜网`
      : "分类未找到 | 52吃瓜网",
    description: category
      ? `${category.name}分区汇总：${category.description}`
      : "该分类暂时不存在，返回 52吃瓜网首页浏览今日热门事件。",
    keywords: category
      ? `${category.name},52吃瓜网,今日热门,${category.tagline},全网热议`
      : "52吃瓜网,分类",
    path: `/category/${slug ?? ""}`,
    jsonLd: category
      ? [
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `${category.name} - 52吃瓜网`,
            description: category.description,
            url: `https://kimchy.cn/category/${category.slug}`,
            inLanguage: "zh-CN",
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "首页",
                item: "https://kimchy.cn/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: category.name,
                item: `https://kimchy.cn/category/${category.slug}`,
              },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: `${category.name}事件列表`,
            itemListElement: list.map((item, index) => ({
              "@type": "ListItem",
              position: index + 1,
              url: `https://kimchy.cn/article/${item.id}`,
              name: item.title,
            })),
          },
        ]
      : undefined,
  });

  if (!category) {
    return (
      <section className="px-4 py-20 text-center md:px-8">
        <p className="font-heading text-5xl font-black text-background-300">404</p>
        <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">
          没有找到这个分类
        </h1>
        <p className="mt-2 text-sm font-label text-foreground-500">
          它可能已经调整或合并，先去首页看看今天的热门事件吧。
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-label text-background-50 transition-colors hover:bg-primary-600"
        >
          返回首页
        </Link>
      </section>
    );
  }

  return (
    <>
      <section className="border-b border-background-200 bg-background-100 px-4 py-8 md:px-8 md:py-10">
        <Breadcrumbs
          items={[{ label: "首页", to: "/" }, { label: category.name }]}
        />
        <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-xl text-primary-700">
                <i className={category.icon}></i>
              </span>
              <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                {category.name}
              </h1>
            </div>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 font-label">
              {category.description}
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-3 text-xs font-label text-foreground-500">
              <span className="flex items-center gap-1.5 rounded-full bg-background-50 px-3 py-1.5">
                <i className="ri-file-list-3-line text-primary-600"></i>
                共 {list.length} 条事件记录
              </span>
              <span className="flex items-center gap-1.5 rounded-full bg-background-50 px-3 py-1.5">
                <i className="ri-refresh-line text-secondary-600"></i>
                持续更新中
              </span>
            </div>
          </div>
          <div className="relative h-40 overflow-hidden rounded-lg bg-background-200 lg:h-full lg:min-h-[180px]">
            <img
              src={category.cover}
              alt={`${category.name}分类封面图 - 52吃瓜网`}
              title={`${category.name} 52吃瓜网`}
              className="h-full w-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      <section className="px-4 py-8 md:px-8 md:py-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-4">
          <div className="lg:col-span-3">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-heading text-lg font-bold text-foreground-950">
                {category.name}事件列表
              </h2>
              <div className="flex rounded-full bg-background-100 p-1">
                {([
                  { key: "latest", label: "按最新" },
                  { key: "hot", label: "按热度" },
                ] as { key: SortKey; label: string }[]).map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => setSort(option.key)}
                    className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-label transition-colors ${
                      sort === option.key
                        ? "bg-background-50 text-primary-700"
                        : "text-foreground-500 hover:text-foreground-800"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              {list.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          </div>

          <aside className="flex flex-col gap-5">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-bar-chart-box-line text-primary-600"></i>
                实时热度榜
              </h3>
              <div className="mt-3">
                <HotRankList limit={6} compact />
              </div>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-layout-grid-line text-secondary-600"></i>
                全部分类
              </h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-label transition-colors ${
                      cat.slug === category.slug
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100 text-foreground-600 hover:bg-background-200"
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}